<template>
  <v-card>
    <v-card-title class="pb-2">
      <v-row>
        <v-col cols="12" class="py-0 headline-2">
          <span v-if="client">
            {{ I18NGetter().useCreateDeal.ADD_DEAL }} {{client.displayName}}
          </span>
          <span v-else>
            {{ I18NGetter().useCreateDeal.NEW_DEAL }}
          </span>
        </v-col>
        <template v-if="!$user.isRealEstateUser">
          <v-col class="pb-0 pt-3" cols="12" v-show="productTypes.length > 1">
            <fp-chip-radio
              v-model="deal.productType"
              item-key="type"
              :id="$cy.addDealProductType"
              :local-storage-key="LAST_PRODUCT_TYPE"
              item-name="name_pl"
              :items="productTypes"
              mandatory
              :class="$onboarding.tours.LEAD.step.INITIAL_FORM"
              ref="initialFormRef"
            />
          </v-col>
        </template>
      </v-row>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="px-0">
      <v-form :ref="CREATE_DEAL_FORM_KEY">
        <v-container grid-list-lg>
          <v-layout wrap class="px-6 pb-3" column>
            <fp-input prepend-icon="edit"
                      v-model.trim="deal.name"
                      required
                      :minLength="3"
                      :id="$cy.dealName"
                      :label="I18NGetter().useCreateDeal.DEAL_NAME"
                      :class="$onboarding.tours.LEAD.step.INITIAL_FORM"/>
            <v-flex xs12>
              <deal-from-partner-autocomplete
                v-model="deal.partner"
                :return-object="true"
                @input="onPartnerPick"
                :disabled="$user.privileges.simplePartnerView || isInitialPartner"
                prepend-icon="mdi-handshake"/>
            </v-flex>
            <template v-if="deal.partner">
              <fp-select prepend-icon="mdi-briefcase"
                         v-if="sourceListForPartner.length > 0"
                         v-model="deal.partnerSourceId"
                         :disabled="isInitialSource"
                         :items="sourceListForPartner"
                         item-key="id"
                         xs12
                         item-name="name"
                         :label="sourceLabel"/>
              <fp-select prepend-icon="person"
                         v-if="salesmanListForPartner.length > 0"
                         v-model="deal.partnerSalesmanId"
                         :items="salesmanListForPartner"
                         item-key="id"
                         xs12
                         item-name="displayName"
                         :label="I18NGetter().useCreateDeal.SALESMAN"/>
            </template>
            <v-flex xs12 v-if="!$user.privileges.simplePartnerView" class="ml-8">
              <fp-checkbox
                :label="I18NGetter().useCreateDeal.BOOK_MEETING"
                :disabled="isInitialPartner && newActivity.requireReporting"
                v-model="createNewActivity"/>
            </v-flex>
          </v-layout>
          <fp-expansion :value="createNewActivity">
            <v-row class="activity-form px-6 pt-3">
              <v-col cols="12">
                <edit-activity-form
                  :hideDealSelection="true"
                  :activity="newActivity"
                />
              </v-col>
            </v-row>
          </fp-expansion>
        </v-container>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-flex tag="v-card-actions" px-6 py-4>
      <loading-button primary :id="$cy.addDeal" :click="() => addDeal()" :class="$onboarding.tours.LEAD.step.INITIAL_FORM">
        {{ I18NGetter().useCreateDeal.ADD_DEAL }}</loading-button>
      <v-spacer></v-spacer>
      <v-btn outlined depressed @click="closeModal()">{{ I18NGetter().useCreateDeal.CANCEL }}</v-btn>
    </v-flex>
  </v-card>
</template>

<script lang="ts">
import {Component, Ref, Watch} from 'vue-property-decorator';
import {VueModalComponent} from 'fp-components';
import Deal from '@/models/Deal';
import {ProductType} from '@/commons/enums/ProductType';
import DealFromPartnerAutocomplete from '@/components/inputs/DealFromPartnerAutocomplete.vue';
import {DealStage} from '@/modules/deals/enums/DealStage';
import {DealStatus} from '@/modules/deals/enums/DealStatus';
import Client from '@/models/Client';
import {IMultiformIconDictionaryElement} from '@/modules/multiForm/shared/IMultiformDictionary';
import DealsApi from '@/modules/deals/services/DealsApi';
import Partner, {FinpackSource, Salesman} from '@/models/Partner';
import DictionaryStore from '@/commons/services/store/DictionaryStore';
import {PartnerType} from '@/modules/partner/enums/PartnerType';
import {Routes} from '@/router/Routes';
import Activity from '@/models/Activity';
import EditActivityForm from '@/modules/activities/views/edit-activity/EditActivityForm.vue';
import {ActivityKind} from '@/modules/activities/enums/ActivityKind';
import FpChipRadio from '@/components/inputs/FpChipRadio.vue';
import EnvironmentProducts from '@/env/services/EnvironmentProducts';
import {capitalize} from '@/filters/StringFilters';
import {Utils} from '@/services/utils/BasicUtils';
import {UserLocalStorage} from '@/services/UserLocalStorage';
import PartnerApi from '@/modules/partner/services/PartnerApi';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {I18NInterface} from '@/services/enumTranslator/I18NInterface';
import {useTour} from '@/composables/useTour';
import {ComponentPublicInstance} from 'vue';

const LAST_PRODUCT_TYPE: string = 'LAST_PRODUCT_TYPE';

@Component({
  components: {FpChipRadio, EditActivityForm, DealFromPartnerAutocomplete, },
})
export default class CreateDeal extends VueModalComponent<Deal> {
  public deal: Deal = new Deal({
    status: DealStatus.DEAL_CREATED,
    stage: DealStage.PENDING,
  });

  public I18NGetter: () => I18NInterface = I18NGetter;
  public isInitialPartner: boolean = false;
  public isInitialSource: boolean = false;
  public oldDeal: Deal = new Deal();
  public disabledTypeOptions: ProductType[] = [];
  public client: Client | null = null;
  public partnersList: Array<Partner> = [];
  public createNewActivity: boolean = false;
  public newActivity: Activity = new Activity({
    kind: ActivityKind.MEETING,
    name: I18NGetter().useCreateDeal.MEETING,
  });

  public readonly CREATE_DEAL_FORM_KEY: string = 'CREATE_DEAL_FORM_KEY';
  public readonly LAST_PRODUCT_TYPE: string = LAST_PRODUCT_TYPE;

  onPartnerPick(newPartner: Nullable<Partner>) {
    const needToCreateNewActivity = newPartner?.requireReporting &&
      !this.$user.privileges.requireBankSecrecy && !this.$user.privileges.simplePartnerView;
    if (needToCreateNewActivity) {
      this.createNewActivity = true;
    }
    this.newActivity.requireReporting = newPartner?.requireReporting;
  }

  @Watch('deal.productType',{ immediate: true, })
  setActivityProductType() {
    this.newActivity.productType = this.deal.productType;
  }

  @Ref() readonly initialFormRef: ComponentPublicInstance | undefined

  async mounted() {
    if (await this.$onboarding.tours.LEAD.isEnabled()) {
      useTour(this.initialFormRef!, this.$onboarding.tours.LEAD.step.INITIAL_FORM);
    }

    await this.getPartners();
    this.client = this.modalData.passedClient;
    this.disabledTypeOptions = this.modalData.disabledTypeOptions || [];
    if (this.modalData.deal) {
      this.deal = this.modalData.deal;
      this.oldDeal = this.deal;
    }
    if (this.client) {
      this.deal.clientIds = [this.client.id,];
      this.deal.clients = [this.client,];
      this.newActivity.client = this.client;
    }
    const productType = UserLocalStorage.getItem(this.LAST_PRODUCT_TYPE);
    const lastProductType: ProductType = Utils.parseJson<ProductType>(productType) as ProductType;
    if (lastProductType && this.productTypes.map(x => x.type).includes(lastProductType)) {
      this.deal.productType = lastProductType;
    } else if (this.productTypes.length > 0) {
      this.deal.productType = this.productTypes[0].type;
    }
    if (this.$user.isRealEstateUser) {
      this.deal.productType = ProductType.REAL_ESTATE;
    }
    if (this.client?.partnerId) {
      this.deal.partnerId = this.client.partnerId;
      this.deal.partner = await PartnerApi.getPartner(this.client.partnerId);
      this.isInitialPartner = Boolean(this.deal.partner);
      if (this.client.partnerSourceId) {
        this.deal.partnerSourceId = this.client.partnerSourceId;
        this.deal.partnerSource = this.deal.partner?.sources.find(x => x.id === this.client!.partnerSourceId);
        this.isInitialSource = Boolean(this.deal.partnerSource);
      }
      this.onPartnerPick(this.deal.partner);
    }
  }

  public async addDeal(): Promise<void> {
    if (this.$validate(this.CREATE_DEAL_FORM_KEY)) {
      if (this.deal.partner?.id) {
        this.deal.partnerId = this.deal.partner?.id;
        delete this.deal.partner;
      }
      try {
        let response: Deal;
        if (this.createNewActivity) {
          const {deal} = await DealsApi.createDealWithActivity(this.deal, this.newActivity);
          response = deal;
          this.$snackbarService.openSuccessSnackbar(I18NGetter().useCreateDeal.CREATED_NEW_DEAL_MEETING);
        } else {
          response = await DealsApi.addDeal(this.deal);
          this.$snackbarService.openSuccessSnackbar(I18NGetter().useCreateDeal.CREATED_NEW_DEAL);
        }
        this.$goto.deal(response.id, response.dealWizardStep, Routes.DEALS.DEAL_DETAILS_TAB.PREVIEW);
        this.closeModal();
      } catch (e) {
        this.errorHandler(e, I18NGetter().useCreateDeal.ERROR_HANDLER_NEW_DEAL);
      }
    }
  }

  public get productTypes(): IMultiformIconDictionaryElement<ProductType>[] {
    return EnvironmentProducts()
      .filter(x => x.isAbleToPerform.deal)
      .filter(x => x.type !== ProductType.SME || !this.$user.isAppDomain.NESTO_RO)
      .map(x => ({
        type: x.type,
        name_pl: capitalize(x.nominative?.female),
        icon: x.icon,
        color: x.color,
      }))
      .filter(x => !this.client ? true
        : this.client?.isCompany ? x.type === ProductType.SME : x.type !== ProductType.SME);
  }

  public async getPartners() {
    this.partnersList = await DictionaryStore.getDictionary('partnerDictionary');
    if (this.$user.privileges.simplePartnerView && this.$user.defaultPartnerId) {
      const defaultPartner = await PartnerApi.getPartner(this.$user.defaultPartnerId);
      this.partnersList = [defaultPartner,];
    }
  }

  public get sourceListForPartner(): Nullable<Array<FinpackSource>> {
    return this.deal?.partner?.id! ? this.partnersList.find(x => x.id === this.deal?.partner?.id!)!.sources : null;
  }

  public get salesmanListForPartner(): Nullable<Array<Salesman>> {
    return this.deal?.partner?.id! ? this.partnersList.find(x => x.id === this.deal?.partner?.id!)!.salesman! : null;
  }

  public get sourceLabel(): string {
    const chosenPartner = this.partnersList.find(x => x.id === this.deal?.partner?.id!)!;
    return chosenPartner.type === PartnerType.INTERNET_WEBSITE
      ? I18NGetter().useCreateDeal.WHAT_WEBSITE : I18NGetter().useCreateDeal.WHAT_INVESTMENT;
  }
}
</script>

<style scoped lang="scss">
  .activity-form{
    background-color: var(--v-primaryBg-base)
  }
  ::v-deep div.fp-radio-group.radio-row {
    margin-top: 0;
    padding-top: 0;
  }
</style>
